/**
 * Herramienta del proveedor: generar licencias a partir del Machine ID del cliente.
 * Ruta: /generador-licencias (no aparece en el menú; uso interno).
 * Requiere LICENSE_GENERATOR_KEY en el servidor (.env del proveedor).
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Key, ContentCopy } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';
import {
  VIGENCIA_PRESETS,
  VigenciaPreset,
  calcularExpiresAt,
  formatearFechaVencimiento,
} from '../utils/licenseVigencia';

const STORAGE_PIN = 'license_generator_pin';

const GeneradorLicencias: React.FC = () => {
  const [pin, setPin] = useState(() => localStorage.getItem(STORAGE_PIN) || '');
  const [machineId, setMachineId] = useState('');
  const [customer, setCustomer] = useState('');
  const [vigenciaPreset, setVigenciaPreset] = useState<VigenciaPreset>('365');
  const [vigenciaDiasCustom, setVigenciaDiasCustom] = useState('365');
  const [result, setResult] = useState('');
  const [resultExpiresAt, setResultExpiresAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const vigenciaPreview = useMemo(
    () => formatearFechaVencimiento(calcularExpiresAt(vigenciaPreset, Number(vigenciaDiasCustom))),
    [vigenciaPreset, vigenciaDiasCustom]
  );

  const handleGenerate = async () => {
    if (!machineId.trim() || !pin.trim()) {
      setError('Indique el ID de máquina del cliente y su clave de generador');
      return;
    }
    setLoading(true);
    setError(null);
    setResult('');
    setResultExpiresAt(null);
    try {
      localStorage.setItem(STORAGE_PIN, pin.trim());
      const expires_at = calcularExpiresAt(vigenciaPreset, Number(vigenciaDiasCustom));
      const res = await axios.post(
        `${API_URL}/licensing/generate`,
        {
          machine_id: machineId.trim().toUpperCase(),
          customer: customer.trim() || undefined,
          expires_at,
        },
        { headers: { 'X-License-Generator-Key': pin.trim() } }
      );
      setResult(res.data.license_key || '');
      setResultExpiresAt(res.data.expiresAt ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'No se pudo generar. Revise la clave de generador y el backend.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 640, width: '100%' }} elevation={3}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Key color="primary" />
          Generador de licencias
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Herramienta para el proveedor del software. El cliente le envía el <strong>ID de máquina</strong> que
          aparece en su pantalla de activación; usted genera la licencia y se la envía para que la pegue en el sistema.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Clave de generador (solo proveedor)"
              type="password"
              fullWidth
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              helperText="Debe coincidir con LICENSE_GENERATOR_KEY en el servidor"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="ID de máquina del cliente"
              fullWidth
              value={machineId}
              onChange={(e) => setMachineId(e.target.value.toUpperCase())}
              placeholder="Ej. A14A6239CF399518E41F7D5D34B8271A"
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Cliente / laboratorio"
              fullWidth
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="vigencia-gen-label">Vigencia</InputLabel>
              <Select
                labelId="vigencia-gen-label"
                label="Vigencia"
                value={vigenciaPreset}
                onChange={(e) => setVigenciaPreset(e.target.value as VigenciaPreset)}
              >
                {VIGENCIA_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {vigenciaPreset === 'custom' && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Días de vigencia"
                type="number"
                fullWidth
                value={vigenciaDiasCustom}
                onChange={(e) => setVigenciaDiasCustom(e.target.value)}
                inputProps={{ min: 1, max: 3650 }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              La licencia vencerá el: <strong>{vigenciaPreview}</strong>
            </Typography>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={handleGenerate} disabled={loading}>
          {loading ? 'Generando…' : 'Generar licencia'}
        </Button>

        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Licencia generada (envíela al cliente)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Vigencia: {formatearFechaVencimiento(resultExpiresAt)}
            </Typography>
            <TextField fullWidth multiline minRows={5} value={result} InputProps={{ readOnly: true }} />
            <Button
              startIcon={<ContentCopy />}
              sx={{ mt: 1 }}
              onClick={() => navigator.clipboard.writeText(result)}
            >
              Copiar licencia
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default GeneradorLicencias;
